import React, {useEffect, useState} from "react";
import axios from "axios";
import {Alert, Modal, Spinner} from "react-bootstrap";
import {formatChangeText, formatLogTimestamp, formatSourceText, formatSystemLabel, systemBadgeVariant} from "../functions/stockChangeLogFormat.js";

/**
 * Fenster mit der vollständigen Änderungs-Historie EINES Artikels
 * (stock_change_log), neueste zuerst - Einstieg z. B. über das
 * Verlaufs-Symbol in der Lagerübersicht (Pages/Stock/Overview.jsx).
 *
 * Lädt erst beim Öffnen (nicht vorab je Zeile) - bei tausenden Artikeln in
 * der Übersicht wäre ein Vorab-Laden der Historie jeder Zeile unnötiger
 * Ballast.
 *
 * @param {boolean} show
 * @param {() => void} onHide
 * @param {string|null} artikelnummer - Artikel, dessen Verlauf gezeigt wird. `null` = Modal bleibt geschlossen.
 */
export default function ArticleHistoryModal({show, onHide, artikelnummer}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [bezeichnung, setBezeichnung] = useState(null);

    useEffect(() => {
        if (!show || !artikelnummer) {
            return undefined;
        }

        const controller = new AbortController();
        setLoading(true);
        setError(null);

        axios
            .get("/stock/change-log/article-history", {
                params: {artikelnummer},
                signal: controller.signal,
            })
            .then(({data}) => {
                setRows(Array.isArray(data?.rows) ? data.rows : []);
                setBezeichnung(data?.bezeichnung ?? null);
            })
            .catch((e) => {
                if (axios.isCancel(e)) {
                    return;
                }
                setError(e.response?.data?.message || e.message || "Der Verlauf konnte nicht geladen werden.");
            })
            .finally(() => {
                setLoading(false);
            });

        return () => controller.abort();
    }, [show, artikelnummer]);

    // Beim Schließen den Stand der letzten Abfrage verwerfen - sonst würde
    // beim nächsten Öffnen (ggf. für einen anderen Artikel) kurz der alte
    // Inhalt aufblitzen, bevor die neue Antwort da ist.
    useEffect(() => {
        if (!show) {
            setRows([]);
            setBezeichnung(null);
            setError(null);
        }
    }, [show]);

    return (
        <Modal show={show} onHide={onHide} centered size="lg" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    Änderungsverlauf: {artikelnummer}
                    {bezeichnung && <span className="text-muted fw-normal"> – {bezeichnung}</span>}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading && (
                    <div className="d-flex align-items-center gap-2 text-muted py-4 justify-content-center">
                        <Spinner animation="border" role="status" style={{width: "1rem", height: "1rem"}} />
                        Verlauf wird geladen…
                    </div>
                )}

                {!loading && error && <Alert variant="danger">{error}</Alert>}

                {!loading && !error && rows.length === 0 && (
                    <Alert variant="light" className="border mb-0">
                        Für diesen Artikel wurde noch keine Änderung protokolliert.
                    </Alert>
                )}

                {!loading && !error && rows.length > 0 && (
                    <ul className="list-unstyled mb-0">
                        {rows.map((row) => (
                            <li key={row.id} className="border-bottom py-2">
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span className="text-muted small text-nowrap">{formatLogTimestamp(row.changed_at)}</span>
                                    <span className={`badge bg-${systemBadgeVariant(row.system)}`}>
                                        {formatSystemLabel(row.system)}
                                    </span>
                                    <span className="fw-semibold">{formatChangeText(row)}</span>
                                </div>
                                <div className="small text-muted mt-1">
                                    {formatSourceText(row)}
                                    {row.trigger && <span> · {row.trigger}</span>}
                                </div>
                                {row.reason && <div className="small mt-1">{row.reason}</div>}
                            </li>
                        ))}
                    </ul>
                )}
            </Modal.Body>
        </Modal>
    );
}
